import { PhoneRevealCreditsGate } from "@/components/contact-reveal/phone-reveal-credits-gate";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useCredits } from "@/contexts/credits-provider";
import { useApi } from "@/hooks/useApi";
import { useChatActionEmitter } from "@/hooks/useChatActions";
import { useMarqueeCopy } from "@/hooks/useMarqueeCopy";
import { useTranslation } from "@/hooks/useTranslation";
import { executeWithMinimumTime } from "@/lib/execute-with-minimum-time";
import { cn } from "@/lib/utils";
import { ReloadIcon } from "@radix-ui/react-icons";
import { EmployeeDTO } from "@shaarp/common";
import { AxiosError } from "axios";
import { EllipsisIcon, Phone } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { FiPhone } from "react-icons/fi";
import { RiErrorWarningLine, RiRobot2Line } from "react-icons/ri";
import { NavLink } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { CopyableContactButton } from "./copyable-contact-button";
import { SignalPhoneDialog } from "./dialog-signal-phone";

// The phone provider enriches asynchronously: the API returns PENDING while it
// works, so the client polls this endpoint until a number or NOT_FOUND comes
// back. Bounded so a provider that never resolves doesn't poll forever.
const PHONE_POLL_INTERVAL_MS = 3000;
const PHONE_POLL_MAX_DURATION_MS = 3 * 60 * 1000;
// Silent auto-resume polls longer than an interactive reveal (the row is
// already mid-enrichment server-side), but is still bounded so a provider that
// never resolves doesn't poll forever in the background. On expiry the loop
// stops and re-arms on the next remount / refetch — the same recovery path
// auto-resume already relies on (pagination, navigation, table refresh).
const SILENT_PHONE_POLL_MAX_DURATION_MS = 15 * 60 * 1000;

export default function DataTableCellCompanyEmployeePhone({
  employee,
  companyId,
  onPhoneRevealed = () => {},
  onCreditsPurchased = () => {},
  compact = false,
  forceContactActions = false,
  isRevealLocked = false,
}: {
  employee: EmployeeDTO;
  companyId?: string;
  onPhoneRevealed?: (
    update: Partial<EmployeeDTO> & { id: string }
  ) => void;
  onCreditsPurchased?: () => void;
  compact?: boolean;
  /**
   * When true (e.g. search results), always offer the reveal button instead of
   * trusting the provider-reported status. Once a reveal has been attempted,
   * the regular status rules apply again.
   */
  forceContactActions?: boolean;
  /** Shows the reveal button disabled: a row about to be replaced. */
  isRevealLocked?: boolean;
}) {
  const marquee = useMarqueeCopy();
  const { t } = useTranslation();
  const { sendChatMessage } = useChatActionEmitter();
  const [showSignalDialog, setShowSignalDialog] = useState(false);
  const [hasAttempted, setHasAttempted] = useState(false);

  const generatePromptAction = () => {
    sendChatMessage(
      t("components.dataTableCompanyEmployees.phone.promptMessage", {
        firstName: employee.firstName,
        lastName: employee.lastName,
        phone: employee.phone,
        jobTitle: employee.jobTitle,
      })
    );
  };

  // The server resolves the phone status per team: it stays "UNKNOWN" until
  // this team attempts a reveal, then reflects the persisted outcome
  // (PENDING / NOT_FOUND / VERIFIED / SIGNAL_WRONG). Once a reveal exists in
  // the DB, that outcome must survive a page refresh, so only force the reveal
  // affordance while the team has not attempted yet — `hasAttempted` alone is
  // local state and is lost on reload.
  const hasTeamAttemptedReveal =
    employee.phoneStatus != null && employee.phoneStatus !== "UNKNOWN";

  const canForceReveal =
    forceContactActions &&
    !hasAttempted &&
    !hasTeamAttemptedReveal &&
    !employee.isBlacklisted;

  if (employee.isBlacklisted) {
    return t("components.dataTableCompanyEmployees.phone.notAvailable");
  }

  if (!canForceReveal && employee.phoneStatus === "NOT_FOUND") {
    return t("components.dataTableCompanyEmployees.phone.notFound");
  }

  if (!canForceReveal && employee.phoneStatus === "SIGNAL_WRONG") {
    return t("components.dataTableCompanyEmployees.phone.notAvailable");
  }

  if (employee.phone) {
    const actionButtons = compact ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="rounded-l-none"
          >
            <EllipsisIcon className="size-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem asChild>
            <NavLink to={`tel:${employee.phone}`}>
              <FiPhone className="size-3" />
              {t("components.dataTableCompanyEmployees.phone.callPhone")}
            </NavLink>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={generatePromptAction}>
            <RiRobot2Line className="size-3" />
            {t("components.dataTableCompanyEmployees.phone.generateHook")}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setShowSignalDialog(true)}
            data-testid="signal-phone-button"
          >
            <RiErrorWarningLine className="size-3" />
            {t("components.dataTableCompanyEmployees.phone.signalPhoneButton")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ) : (
      <>
        <Tooltip>
          <TooltipTrigger asChild>
            <NavLink
              className={cn(
                buttonVariants({ variant: "outline", size: "sm" }),
                "rounded-none"
              )}
              to={`tel:${employee.phone}`}
            >
              <FiPhone className="size-3" />
            </NavLink>
          </TooltipTrigger>
          <TooltipContent>
            {t("components.dataTableCompanyEmployees.phone.callPhone")}
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="rounded-none"
              onClick={generatePromptAction}
            >
              <RiRobot2Line className="size-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {t("components.dataTableCompanyEmployees.phone.generateHook")}
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button
                variant="outline"
                size="sm"
                className="rounded-l-none"
                type="button"
                onClick={() => setShowSignalDialog(true)}
                data-testid="signal-phone-button"
              >
                <RiErrorWarningLine className="w-4 h-4" />
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {t("components.dataTableCompanyEmployees.phone.signalPhoneButton")}
          </TooltipContent>
        </Tooltip>
      </>
    );

    return (
      <div className="flex -space-x-px">
        <CopyableContactButton
          value={employee.phone}
          copiedLabel={t("components.dataTableCompanyEmployees.phone.copied")}
          marquee={marquee}
        />
        {actionButtons}
        <SignalPhoneDialog
          open={showSignalDialog}
          onOpenChange={setShowSignalDialog}
          employee={employee}
          companyId={companyId}
          onReported={(motive: string) =>
            handleSignalReported({ employee, motive, onPhoneRevealed })
          }
        />
      </div>
    );
  }

  const isPending = !canForceReveal && employee.phoneStatus === "PENDING";

  return (
    <ButtonRetrievePhone
      employee={employee}
      companyId={companyId}
      autoResumePending={isPending}
      isLocked={isRevealLocked}
      onPhoneRevealed={(update) => {
        setHasAttempted(true);
        onPhoneRevealed(update);
      }}
      onCreditsPurchased={onCreditsPurchased}
    />
  );
}

const ButtonRetrievePhone = ({
  employee,
  companyId,
  autoResumePending = false,
  isLocked = false,
  onPhoneRevealed,
  onCreditsPurchased,
}: {
  employee: EmployeeDTO;
  companyId?: string;
  /**
   * When true, the server reports this row as PENDING: keep the spinner up and
   * resume polling on mount (covers a remount, pagination, or a parent reset
   * that dropped the in-flight loop).
   */
  autoResumePending?: boolean;
  /** Shows the button disabled: a row about to be replaced. */
  isLocked?: boolean;
  onPhoneRevealed: (update: Partial<EmployeeDTO> & { id: string }) => void;
  onCreditsPurchased: () => void;
}) => {
  const api = useApi();
  const { t } = useTranslation();
  const { toast } = useToast();
  const toastError = useToastError();
  const { refreshCredits } = useCredits();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  // Guards against state updates / network polling after the cell unmounts
  // (e.g. pagination or navigation while a reveal is still in flight).
  const isMountedRef = useRef<boolean>(true);
  // Ensures the initial click and the auto-resume effect share a single poller
  // instead of stacking concurrent loops.
  const isPollingRef = useRef<boolean>(false);

  const fetchPhone = async ({
    consumeCredits,
    silent = false,
  }: { consumeCredits?: boolean; silent?: boolean } = {}) => {
    if (isPollingRef.current) return;
    isPollingRef.current = true;
    setIsLoading(true);

    const requestPhone = async (): Promise<EmployeeDTO> => {
      const { data } = await api.get<EmployeeDTO>(
        `/company/${companyId}/employee/${employee.id}/enrich-phone`,
        {
          params: {
            ...(consumeCredits !== undefined && { consumeCredits }),
          },
        }
      );
      return data;
    };

    try {
      // Keep a small minimum on the first call so a fast result doesn't flash;
      // 2s minimum loading time.
      let apiData = await executeWithMinimumTime(requestPhone, 2000);

      // The provider enriches asynchronously and reports PENDING while it works.
      // Both paths are bounded so the cell can't poll forever; the silent
      // auto-resume gets a longer window because the row is already mid-flight
      // server-side. On expiry the loop stops and re-arms on the next remount.
      const deadline =
        Date.now() +
        (silent
          ? SILENT_PHONE_POLL_MAX_DURATION_MS
          : PHONE_POLL_MAX_DURATION_MS);
      while (
        apiData?.phoneStatus === "PENDING" &&
        Date.now() < deadline &&
        isMountedRef.current
      ) {
        await new Promise((resolve) =>
          setTimeout(resolve, PHONE_POLL_INTERVAL_MS)
        );
        apiData = await requestPhone();
      }

      // The endpoint always returns a DTO (known statuses like NOT_FOUND are
      // surfaced inline, not as errors). Guard the body defensively so an empty
      // response never crashes the cell.
      if (isMountedRef.current && apiData) {
        onPhoneRevealed({
          id: employee.id,
          phone: apiData.phone ?? null,
          phoneStatus: apiData.phoneStatus,
        });
      }

      // Only a delivered number debits credits server-side. PENDING polls and
      // NOT_FOUND results consume nothing, so skip the balance round-trip then
      // (the reveal can poll several times before resolving).
      if (apiData?.phone) {
        await refreshCredits();
      }
    } catch (error: unknown) {
      // Auto-resume polls silently: swallow the error with no toast. The spinner
      // stays up (the row is still PENDING) and polling re-arms on the next
      // remount/refetch.
      if (silent) {
        return;
      }
      const status =
        error instanceof AxiosError ? error.response?.status : undefined;
      // Check if it's a throttle error (429 status code)
      if (status === 429) {
        toast({
          variant: "destructive",
          title: t(
            "components.dataTableCompanyEmployees.phone.throttleError.title"
          ),
          description: t(
            "components.dataTableCompanyEmployees.phone.throttleError.description"
          ),
        });
      } else if (status === 403) {
        // A 403 has several server-side causes; the body carries a
        // ContactRevealErrorCode so we surface the matching message instead of
        // always assuming insufficient credits.
        const isInsufficientCredits =
          error instanceof AxiosError &&
          error.response?.data?.code === "INSUFFICIENT_CREDITS";
        toast({
          variant: "destructive",
          title: t(
            isInsufficientCredits
              ? "components.dataTableCompanyEmployees.phone.limitReachedError.title"
              : "components.dataTableCompanyEmployees.phone.revealUnavailableError.title"
          ),
          description: t(
            isInsufficientCredits
              ? "components.dataTableCompanyEmployees.phone.limitReachedError.description"
              : "components.dataTableCompanyEmployees.phone.revealUnavailableError.description"
          ),
        });
      } else {
        toastError();
      }
    } finally {
      isPollingRef.current = false;
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  // Resume polling when the server still reports PENDING and no loop is running
  // (recovers from a remount, pagination, or a parent state reset that dropped
  // the in-flight loop). The silent loop runs on a generous bounded window and
  // re-arms here on the next remount if the row is still PENDING.
  useEffect(() => {
    if (autoResumePending && !isPollingRef.current) {
      void fetchPhone({ silent: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoResumePending]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleRetrievePhoneClick = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmRetrieve = () => {
    fetchPhone();
  };

  const handleConfirmRetrieveWithoutCost = () => {
    fetchPhone({ consumeCredits: false });
  };

  // A PENDING row is mid-enrichment server-side, so show the spinner even
  // before the local poll loop has set isLoading (e.g. right after a remount).
  const showSpinner = isLoading || autoResumePending;

  return (
    <>
      <Button
        variant="default"
        size="sm"
        onClick={handleRetrievePhoneClick}
        disabled={showSpinner || isLocked}
        className="gap-2"
      >
        {showSpinner ? (
          <>
            <ReloadIcon className="w-4 h-4 animate-spin" />
            {t("components.dataTableCompanyEmployees.phone.retrieving")}
          </>
        ) : (
          <>
            <Phone className="size-4" />
            {t("components.dataTableCompanyEmployees.phone.retrievePhone")}
          </>
        )}
      </Button>

      {/* Cost warning / top-up / ask-an-admin, shared with the chat contact card
          so neither surface can debit credits without the same prompt. */}
      <PhoneRevealCreditsGate
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        onConfirm={handleConfirmRetrieve}
        onConfirmWithoutCost={handleConfirmRetrieveWithoutCost}
        onCreditsPurchased={onCreditsPurchased}
      />
    </>
  );
};

const handleSignalReported = ({
  employee,
  motive,
  onPhoneRevealed,
}: {
  employee: EmployeeDTO;
  motive: string;
  onPhoneRevealed: (update: Partial<EmployeeDTO> & { id: string }) => void;
}) => {
  if (motive === "SIGNAL_BAN") {
    onPhoneRevealed({
      id: employee.id,
      isBlacklisted: true,
    });
  } else {
    onPhoneRevealed({
      id: employee.id,
      phoneStatus: "SIGNAL_WRONG",
    });
  }
};
